import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { Mutation, MutationBlockNfcCardArgs, NfcCard, Query } from 'graphql/generated/graphqlTypes';
import { BLOCK_ACCESS_CARD } from 'graphql/v2/mutations';
import { GET_ACCESS_CARDS } from 'graphql/v2/queries';
import { useRegisterBackgroundRefetch } from 'hooks/useBackgroundRefetch/useBackgroundRefetch';
import { useEffect } from 'react';
import { TranslatedHookProps } from '../hooks.types';

const useAccessCards = ({ translation, handleFetchError }: TranslatedHookProps) => {
  const {
    loading: accessCardsLoading,
    error: accessCardsError,
    data: accessCardsData,
    networkStatus: accessCardsNetworkStatus
  } = useQuery<Pick<Query, 'getNfcCards'>>(GET_ACCESS_CARDS, {
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (accessCardsError) {
      handleFetchError(translation.access_card_error_fetching);
      console.error(accessCardsError);
    }
  }, [accessCardsError]);

  const isRefetchingAccessCards = !!accessCardsData && accessCardsNetworkStatus !== NetworkStatus.ready;

  useRegisterBackgroundRefetch(isRefetchingAccessCards);

  const [blockAccessCardMutation, { loading: blockingAccessCard, error: blockAccessCardError }] = useMutation<
    Pick<Mutation, 'blockNfcCard'>,
    MutationBlockNfcCardArgs
  >(BLOCK_ACCESS_CARD, {
    refetchQueries: [GET_ACCESS_CARDS],
    awaitRefetchQueries: true
  });

  useEffect(() => {
    if (blockAccessCardError) {
      handleFetchError(translation.access_card_error_blocking);
      console.error(blockAccessCardError);
    }
  }, [blockAccessCardError]);

  const blockAccessCard = async (uuid: string, reason: string) => {
    await blockAccessCardMutation({ variables: { uuid, reason } });
  };

  return {
    accessCards: (accessCardsData?.getNfcCards || []) as NfcCard[],
    loadingAccessCards: accessCardsLoading && !accessCardsData,
    isRefetchingAccessCards,
    blockAccessCard,
    blockingAccessCard
  };
};

export default useAccessCards;
